import {
  useQuery,
  getArticleWorkflowQueue,
  updateArticleSentiment,
  deleteArticles,
} from "wasp/client/operations";
import { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  Input,
  SearchField,
  Select,
  SurfaceCard,
} from "../../../components/ui";
import { AdminPaginationControl } from "../AdminPaginationControl";
import { ADMIN_DASHBOARD_PAGE_SIZE } from "../adminControlContracts";
import LoadingSpinner from "../../layout/LoadingSpinner";
import type { Article } from "wasp/entities";
import ArticleDetailDrawer from "./ArticleDetailDrawer";

// Articles table reuses shared admin contracts so pagination and filter spacing stay consistent inside this scoped Wave 8 refactor. (DL-001, DL-003)
const SENTIMENT_OPTIONS = [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5].map(
  (value) => ({
    value: String(value),
    label: String(value),
  }),
);
const SENTIMENT_FILTER_ANY_VALUE = "any";
const SENTIMENT_FILTER_OPTIONS = [
  { value: SENTIMENT_FILTER_ANY_VALUE, label: "Any" },
  ...SENTIMENT_OPTIONS,
];

type ArticleWithBookmarks = Article & { bookmarks?: { id: number }[] };

const ArticlesTable = () => {
  const [skip, setSkip] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState<string>("");
  const [sentimentMinFilter, setSentimentMinFilter] = useState(
    SENTIMENT_FILTER_ANY_VALUE,
  );
  const [sentimentMaxFilter, setSentimentMaxFilter] = useState(
    SENTIMENT_FILTER_ANY_VALUE,
  );
  const [provider, setProvider] = useState<string>("");
  const [selectedArticles, setSelectedArticles] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(
    null,
  );
  const sentimentMin =
    sentimentMinFilter === SENTIMENT_FILTER_ANY_VALUE
      ? undefined
      : Number(sentimentMinFilter);
  const sentimentMax =
    sentimentMaxFilter === SENTIMENT_FILTER_ANY_VALUE
      ? undefined
      : Number(sentimentMaxFilter);
  const normalizedSearch = search.trim();
  const normalizedProvider = provider.trim();

  const { data, isLoading, error, refetch } = useQuery(
    getArticleWorkflowQueue,
    {
      skip,
      limit: ADMIN_DASHBOARD_PAGE_SIZE,
      search: normalizedSearch || undefined,
      sentimentMin,
      sentimentMax,
      provider: normalizedProvider || undefined,
    },
  );

  useEffect(() => {
    setPage(1);
  }, [search, sentimentMinFilter, sentimentMaxFilter, provider]);

  useEffect(() => {
    setSkip((page - 1) * ADMIN_DASHBOARD_PAGE_SIZE);
  }, [page, setSkip]);

  const handleSentimentUpdate = async (
    articleId: number,
    newSentiment: number,
  ) => {
    try {
      await updateArticleSentiment({ id: articleId, sentiment: newSentiment });
      refetch();
    } catch (error) {
      console.error("Failed to update sentiment:", error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedArticles.length === 0) return;

    try {
      await deleteArticles({ ids: selectedArticles });
      setSelectedArticles([]);
      setShowDeleteConfirm(false);
      refetch();
    } catch (error) {
      console.error("Failed to delete articles:", error);
    }
  };

  const toggleArticleSelection = (articleId: number) => {
    setSelectedArticles((prev) =>
      prev.includes(articleId)
        ? prev.filter((id) => id !== articleId)
        : [...prev, articleId],
    );
  };

  const toggleSelectAll = () => {
    if (!data?.articles) return;

    if (selectedArticles.length === data.articles.length) {
      setSelectedArticles([]);
    } else {
      setSelectedArticles(data.articles.map((article) => article.id));
    }
  };

  const getSentimentColor = (sentiment: number) => {
    if (sentiment >= 4) return "text-success-600";
    if (sentiment >= 2) return "text-warning-600";
    return "text-danger-600";
  };
  const totalPages = data?.totalPages || 1;
  const hasSelectedArticles = selectedArticles.length > 0;

  if (error) {
    return (
      <SurfaceCard>
        <p className="text-danger-600">
          Failed to load articles: {error.message}
        </p>
      </SurfaceCard>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SurfaceCard padding="none">
        <div className="flex flex-col items-start gap-3 bg-neutral-100/40 p-4 dark:bg-neutral-700/50 sm:p-5">
          <span className="text-sm font-medium">Filters</span>
          <div className="flex w-full flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid w-full gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div>
                <label
                  htmlFor="search-filter"
                  className="mb-2 block text-sm font-medium text-text-primary dark:text-bodydark"
                >
                  Search
                </label>
                <SearchField
                  id="search-filter"
                  value={search}
                  onChange={setSearch}
                  placeholder="Search title or summary..."
                  className="w-full"
                  inputClassName="h-10 rounded-sm border border-stroke bg-white py-2 pl-10 pr-3 dark:border-form-strokedark dark:bg-form-input"
                />
              </div>
              <Input
                id="provider-filter"
                type="text"
                label="Provider"
                placeholder="Provider name..."
                value={provider}
                onChange={(event) => setProvider(event.currentTarget.value)}
                className="h-10 rounded-sm"
              />
              <Select
                label="Sentiment Min"
                value={sentimentMinFilter}
                onChange={setSentimentMinFilter}
                options={SENTIMENT_FILTER_OPTIONS}
                className="h-10 w-full min-w-0 rounded-sm"
              />
              <Select
                label="Sentiment Max"
                value={sentimentMaxFilter}
                onChange={setSentimentMaxFilter}
                options={SENTIMENT_FILTER_OPTIONS}
                className="h-10 w-full min-w-0 rounded-sm"
              />
            </div>

            {!isLoading && (
              <div className="flex w-full flex-wrap items-center justify-start gap-3 xl:w-auto xl:justify-end">
                {hasSelectedArticles && (
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="solid"
                    tone="danger"
                    size="sm"
                    className="rounded-sm"
                  >
                    Delete Selected ({selectedArticles.length})
                  </Button>
                )}
                <AdminPaginationControl
                  page={page}
                  totalPages={totalPages}
                  onPageChange={(nextPage) => setPage(nextPage)}
                  className="justify-start xl:justify-end"
                />
              </div>
            )}
          </div>
        </div>

        <div className="hidden border-t border-stroke px-4 py-3 dark:border-strokedark md:grid md:grid-cols-12 md:px-6">
          <div className="col-span-1 flex items-center">
            <input
              type="checkbox"
              checked={
                data?.articles
                  ? selectedArticles.length === data.articles.length
                  : false
              }
              onChange={toggleSelectAll}
              className="rounded-sm border-stroke"
            />
          </div>
          <div className="col-span-4 flex items-center">
            <p className="font-medium">Title</p>
          </div>
          <div className="col-span-2 flex items-center">
            <p className="font-medium">Provider</p>
          </div>
          <div className="col-span-1 flex items-center">
            <p className="font-medium">Sentiment</p>
          </div>
          <div className="col-span-2 flex items-center">
            <p className="font-medium">Published</p>
          </div>
          <div className="col-span-1 flex items-center">
            <p className="font-medium">Bookmarks</p>
          </div>
          <div className="col-span-1 flex items-center">
            <p className="font-medium">Actions</p>
          </div>
        </div>

        {isLoading && (
          <div className="-mt-40">
            <LoadingSpinner />
          </div>
        )}

        {!!data?.articles &&
          data.articles.length > 0 &&
          data.articles.map((article: ArticleWithBookmarks) => (
            <div
              key={article.id}
              className="grid grid-cols-1 gap-2 border-t border-stroke px-4 py-2.5 dark:border-strokedark md:grid-cols-12 md:gap-2 md:px-6"
            >
              <div className="flex items-center justify-between md:col-span-1 md:justify-start">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Select
                </p>
                <input
                  type="checkbox"
                  checked={selectedArticles.includes(article.id)}
                  onChange={() => toggleArticleSelection(article.id)}
                  className="rounded-sm border-stroke"
                />
              </div>

              <div className="flex flex-col gap-1 md:col-span-4 md:justify-center">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Title
                </p>
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-medium text-black dark:text-white line-clamp-2">
                    {article.title}
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400 line-clamp-1">
                    {article.summary}
                  </p>
                  <a
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-info hover:text-info-800 dark:text-info-400"
                  >
                    Source
                  </a>
                </div>
              </div>

              <div className="flex flex-col gap-1 md:col-span-2 md:justify-center">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Provider
                </p>
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm text-black dark:text-white">
                    {article.provider}
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">
                    {article.feedName}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between md:col-span-1 md:justify-start">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Sentiment
                </p>
                <Select
                  value={String(article.sentiment)}
                  onChange={(value) =>
                    handleSentimentUpdate(article.id, Number(value))
                  }
                  options={SENTIMENT_OPTIONS}
                  className={`h-8 min-w-0 gap-1 rounded-sm px-1.5 py-1 text-sm font-medium ${getSentimentColor(
                    article.sentiment,
                  )}`}
                />
              </div>

              <div className="flex flex-col gap-1 md:col-span-2 md:justify-center">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Published
                </p>
                <p className="text-sm text-black dark:text-white">
                  {new Date(article.publishedAt).toLocaleDateString()}
                  <br />
                  <span className="text-xs text-neutral-600 dark:text-neutral-400">
                    {new Date(article.publishedAt).toLocaleTimeString()}
                  </span>
                </p>
              </div>

              <div className="flex items-center justify-between md:col-span-1 md:justify-start">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Bookmarks
                </p>
                <p className="text-sm text-black dark:text-white">
                  {article.bookmarks?.length || 0}
                </p>
              </div>

              <div className="flex items-center justify-between md:col-span-1 md:justify-start">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Actions
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    tone="neutral"
                    size="xs"
                    className="rounded-sm px-2 py-1"
                    onClick={() => setSelectedArticleId(article.id)}
                  >
                    Review
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedArticles([article.id]);
                      setShowDeleteConfirm(true);
                    }}
                    variant="ghost"
                    tone="danger"
                    size="xs"
                    className="rounded-sm px-2 py-1"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}

        {!isLoading && (!data?.articles || data.articles.length === 0) && (
          <div className="border-t border-stroke px-4 py-6 text-center dark:border-strokedark md:px-6">
            <p className="text-neutral-600 dark:text-neutral-400">
              No articles found matching the current filters.
            </p>
          </div>
        )}

        {!isLoading && data && (
          <div className="border-t border-stroke py-3 px-6 dark:border-strokedark">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Showing {data.articles.length} of {data.totalCount} articles
            </p>
          </div>
        )}
      </SurfaceCard>

      <ArticleDetailDrawer
        articleId={selectedArticleId}
        onClose={() => setSelectedArticleId(null)}
      />

      <Dialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Confirm Deletion"
        description={`Are you sure you want to delete ${selectedArticles.length} article(s)? This action cannot be undone.`}
      >
        <div className="flex justify-end gap-3">
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            variant="outline"
            tone="neutral"
            size="sm"
            className="rounded-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            tone="danger"
            size="sm"
            className="rounded-sm"
          >
            Delete
          </Button>
        </div>
      </Dialog>
    </div>
  );
};

export default ArticlesTable;
